import { RaThemeOptions, defaultTheme } from 'react-admin';
import { createTheme } from '@mui/material';

export const sidebar = {
  width: 264,
  closedWidth: 55,
};

export const primary = {
  main: '#0096D4',
  dark: '#007DB1',
  light: '#06a5e7',
};
export const secondary = {
  main: '#EF7D00',
  dark: '#dc7606',
  light: '#F59C00',
};
export const info = {
  main: '#1976D2',
  dark: '#115293',
  light: '#4791DB',
  50: '#FCFDFE',
  100: '#F7F8FC',
  200: '#F0F1F7',
  300: '#EBEDF0',
  400: '#DFE0EB',
  500: '#C5C7CD',
  600: '#9FA2B4',
  700: '#787579',
  800: '#4B506D',
  900: '#252733',
};
export const success = {
  main: '#4CAF50',
  dark: '#388E3C',
  light: '#81C784',
};
export const warning = {
  main: '#FF9800',
  dark: '#F57C00',
  light: '#FEC400',
  contrastText: '#FFFFFF',
};
export const error = {
  main: '#F44336',
  dark: '#D32F2F',
  light: '#E57373',
};
const white = {
  main: '#EEEDEE',
  dark: '#CBC9CB',
  light: '#F9F9F9',
};
export const grey = {
  50: '#F9F9F9',
  100: '#EAEAEA',
  200: '#DCDBDC',
  300: '#CBC9CB',
  400: '#B9B7B9',
  500: '#8B878B',
  600: '#7A767A',
  700: '#686568',
  800: '#575457',
  900: '#464346',
  A100: '#EEEDEE',
  A200: '#DCDBDC',
  A300: '#CBC9CB',
  A400: '#B9B7B9',
  A700: '#686568',
};
const breakpoints = {
  xs: 0,
  sm: 800,
  md: 1200,
  lg: 1536,
  xl: 1920,
};

export const theme = createTheme({
  ...defaultTheme,
  sidebar,
  breakpoints: {
    ...defaultTheme.breakpoints,
    values: breakpoints,
  },
  palette: {
    ...defaultTheme.palette,
    primary,
    secondary,
    info,
    success,
    warning,
    error,
    grey,
    white,
  },
  typography: {
    ...defaultTheme.typography,
    fontFamily: 'Mulish, sans-serif',
    h1: {
      fontWeight: '600',
      fontSize: '47.78px',
      lineHeight: '57.3px',
      letterSpacing: '-1.5px',
    },
    h2: {
      fontWeight: '600',
      fontSize: '39.81px',
      lineHeight: '48px',
      letterSpacing: '-1px',
    },
    h3: {
      fontWeight: '600',
      fontSize: '33.18px',
      lineHeight: '40px',
      letterSpacing: '-0.25px',
    },
    h4: {
      fontWeight: '600',
      fontSize: '27.65px',
      lineHeight: '33px',
      letterSpacing: '-0.25px',
    },
    h5: {
      fontWeight: '600',
      fontSize: '23.04px',
      lineHeight: '28px',
      letterSpacing: '-0.25px',
    },
    h6: {
      fontWeight: '600',
      fontSize: '19.2px',
      lineHeight: '23px',
      letterSpacing: '0.15px',
    },
    subtitle1: {
      fontWeight: '700',
      fontSize: '16px',
      lineHeight: '19px',
      letterSpacing: '-0.15px',
    },
    subtitle2: {
      fontWeight: '700',
      fontSize: '14px',
      lineHeight: '17px',
      letterSpacing: '-0.1px',
    },
    body1: {
      fontWeight: '400',
      fontSize: '16px',
      lineHeight: '19px',
      letterSpacing: '-0.15px',
    },
    body2: {
      fontWeight: '400',
      fontSize: '14px',
      lineHeight: '17px',
      letterSpacing: '-0.17px',
    },
    caption: {
      fontWeight: '400',
      fontSize: '12px',
      lineHeight: '14px',
      letterSpacing: '0.4px',
    },
    overline: {
      fontWeight: '400',
      fontSize: '12px',
      lineHeight: '14px',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
    button: {
      fontWeight: '400',
      fontSize: '14px',
      lineHeight: '17px',
      letterSpacing: '0.4px',
    },
  },
  components: {
    ...defaultTheme.components,
    RaBreadcrumbItem: {
      styleOverrides: {
        root: {
          '&&&': {
            color: '#000',
            '& .MuiTypography-root': {
              fontSize: '20px',
            },
          },
        },
      },
    },
    RaLayout: {
      styleOverrides: {
        root: {
          backgroundColor: info[100],
          '& .RaLayout-content': {
            backgroundColor: info[100],
            [`@media (min-width: ${breakpoints.xs}px)`]: {
              paddingTop: '16px',
              paddingLeft: '8px',
              paddingRight: '8px',
              paddingBottom: '24px',
            },
            [`@media (min-width: ${breakpoints.sm}px)`]: {
              paddingLeft: '24px',
              paddingRight: '12px',
            },
          },
        },
        content: {},
      },
    },
    RaMenu: {
      styleOverrides: {
        open: {
          width: sidebar.width,
        },
      },
    },

    RaMenuItemLink: {
      styleOverrides: {
        root: {
          padding: '16px',
          color: '#fff',
        },
        active: {
          borderLeft: '3px solid #fff',
          paddingLeft: '13px',
          color: '#fff',
          backgroundColor: primary.main,
          '&:hover': {
            backgroundColor: primary.dark,
          },
        },
        icon: {
          color: '#fff',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          height: '42px',
        },
        sizeSmall: {
          height: '36px',
        },
        sizeLarge: {
          height: '48px',
        },
      },
    },
    RaRadioButtonGroupInput: {
      styleOverrides: {
        label: {
          color: '#4B506D',
          marginBottom: '4px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        asterisk: {
          '&.Mui-error': {
            color: info[800],
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: '26px',
          '& .MuiFormHelperText-contained': {
            marginRight: 0,
            marginLeft: 0,
            position: 'absolute',
            bottom: '100%',
            right: 0,
            maxWidth: '70%',
            pointerEvents: 'none',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        contained: {
          marginLeft: 0,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '16px 16px 0',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '8px 16px',
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '0 8px 16px',
        },
      },
    },
    RaFormInput: {
      styleOverrides: {
        input: {
          width: '700px',
        },
      },
    },
    RaNotification: {
      styleOverrides: {
        success: {
          color: 'white',
        },
      },
    },
  },
} as RaThemeOptions);

export const defaultBoxShadow =
  '0px 0px 1px rgba(26, 32, 36, 0.32), 0px 1px 2px rgba(91, 104, 113, 0.32)';
export const hoverBoxShadow = `0px 0px 1px ${primary.light}, 0px 1px 2px ${primary.dark}, 0px -1px 2px ${primary.dark}`;

export default theme;
